import Image from "next/image";
/* eslint-disable react/jsx-key */
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import gsap from "gsap";
import SplitText from "gsap/src/SplitText";
import ScrollTrigger from "gsap/ScrollTrigger";
import { PrevButton, NextButton, usePrevNextButtons } from "./Carousel/EmblaCarouselArrowButtons"
import useEmblaCarousel from "embla-carousel-react"
import { Send } from "lucide-react";

gsap.registerPlugin(SplitText, ScrollTrigger);

export const SectionTestimonials = () => {

    const subheadlineBoxRef = useRef()
    const titleRef = useRef()
    const emblaWrapperRef = useRef()

    // GSAP ANIMATIONS

    useEffect(() => {

        // subheadline box animation
        gsap.to(subheadlineBoxRef.current, { opacity: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power1', scrollTrigger: { trigger: subheadlineBoxRef.current, start: "top 95%" } });

        // headline text animation
        const titleSplit = new SplitText(titleRef.current, { type: "words" });
        gsap.fromTo(titleSplit.words, { 'will-change': 'opacity, transform', filter: 'blur(8px)', opacity: 0, yPercent: 50 }, { opacity: 1, filter: 'blur(0px)', yPercent: 0, stagger: 0.05, duration: 0.75, ease: "power2", scrollTrigger: { trigger: titleRef.current, start: "top 95%" } });

        // embla wrapper animation
        gsap.to(emblaWrapperRef.current, { opacity: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power1', scrollTrigger: { trigger: emblaWrapperRef.current, start: "top 95%" } });

    }, [])

    // EMBLA CAROUSEL

    const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true })
    const [scrollProgress, setScrollProgress] = useState(0)

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick
    } = usePrevNextButtons(emblaApi)

    const onScroll = useCallback((emblaApi) => {
        const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
        setScrollProgress(progress * 100)
    }, [])

    useEffect(() => {
        if (!emblaApi) return

        onScroll(emblaApi)
        emblaApi
            .on("reInit", onScroll)
            .on("scroll", onScroll)
            .on("slideFocus", onScroll)
    }, [emblaApi, onScroll])

    return (
        <section className="testimonials">
            <div className="testimonials-content">
                <div className="textbox testimonials-content-textbox">
                    <div className="subheadline-box opacity-blur" ref={subheadlineBoxRef} >
                        <Send className="subheadline-box-icon" />
                        <h2 className="small-description grey" >Client Success Stories</h2>
                    </div>
                    <div className="titlebox">
                        <div className="titlebox-big-gradient" />
                        <h2 className="subheadline white" ref={titleRef} >Built for Excellence, <br />Designed for Impact</h2>
                    </div>
                </div>
                <div className="opacity-blur" ref={emblaWrapperRef} >
                    <div className="testimonials-carousel" ref={emblaRef} >
                        <div className="testimonials-carousel-row">
                            <div className="testimonials-item-padding" />
                            <div className="testimonials-item" >
                                <div className="testimonials-item-content">
                                    <div className="testimonials-item-profile">
                                        <Image src="https://ui-avatars.com/api/?name=Tanish&background=0D0D0D&color=fff" alt="Tanish"  width={128} height={128} sizes="(max-width: 768px) 10vw, 5vw" />
                                    </div>
                                    <div className="testimonials-item-center">
                                        <p className="big-description white" >Tanish</p>
                                        <p className="description grey" >Artistic Visionary</p>
                                    </div>
                                    <p className="description white" >Quroix Labs completely elevated our artistic page. Their design perfectly captured our brand&apos;s essence, blending stunning visuals with a seamless user experience that our audience loves.</p>
                                </div>
                                <div className="testimonials-item-grid" />
                            </div>
                            <div className="testimonials-item" >
                                <div className="testimonials-item-content">
                                    <div className="testimonials-item-profile">
                                        <Image src="https://ui-avatars.com/api/?name=Harishvaran&background=0D0D0D&color=fff" alt="Harishvaran"  width={128} height={128} sizes="(max-width: 768px) 10vw, 5vw" />
                                    </div>
                                    <div className="testimonials-item-center">
                                        <p className="big-description white" >Harishvaran</p>
                                        <p className="description grey" >Founder, SMMA Agency</p>
                                    </div>
                                    <p className="description white" >The website they built for our SMMA agency has been a game-changer. It not only looks highly professional but has significantly boosted our lead generation and conversion rates. Highly recommended!</p>
                                </div>
                                <div className="testimonials-item-grid" />
                            </div>
                            <div className="testimonials-item" >
                                <div className="testimonials-item-content">
                                    <div className="testimonials-item-profile">
                                        <Image src="https://ui-avatars.com/api/?name=Elena+Rostova&background=0D0D0D&color=fff" alt="Elena Rostova"  width={128} height={128} sizes="(max-width: 768px) 10vw, 5vw" />
                                    </div>
                                    <div className="testimonials-item-center">
                                        <p className="big-description white" >Elena Rostova</p>
                                        <p className="description grey" >E-Commerce Director</p>
                                    </div>
                                    <p className="description white" >The custom e-commerce platform delivered by the team was beyond our expectations. It&apos;s incredibly fast, reliable, and the intuitive backend has saved us countless hours of manual work.</p>
                                </div>
                                <div className="testimonials-item-grid" />
                            </div>
                            <div className="testimonials-item" >
                                <div className="testimonials-item-content">
                                    <div className="testimonials-item-profile">
                                        <Image src="https://ui-avatars.com/api/?name=Marcus+Thorne&background=0D0D0D&color=fff" alt="Marcus Thorne"  width={128} height={128} sizes="(max-width: 768px) 10vw, 5vw" />
                                    </div>
                                    <div className="testimonials-item-center">
                                        <p className="big-description white" >Marcus Thorne</p>
                                        <p className="description grey" >Head of Operations</p>
                                    </div>
                                    <p className="description white" >We hired them to automate our internal workflows, and the transformation has been phenomenal. Their tailored software solutions eliminated our bottlenecks and streamlined our entire process.</p>
                                </div>
                                <div className="testimonials-item-grid" />
                            </div>
                            <div className="testimonials-item testimonials-item-last" >
                                <div className="testimonials-item-content testimonials-item-content-last">
                                    <div className="testimonials-item-last-top">
                                        <p className="description white" >Ready to transform your operations?</p>
                                    </div>
                                    <p className="small-subheadline white" >Let&apos;s discuss your project</p>
                                    <Link href="/contact" className="contact-button-wrapper" style={{ textDecoration: 'none' }}>
                                        <div className="contact-button-white" >
                                            <span>
                                                <span className="contact-button-container-white">
                                                    <span className="contact-button-primary-white"></span>
                                                    <span className="contact-button-complimentary-white"></span>
                                                </span>
                                            </span>
                                            <span className="description black" >Book a call</span>
                                        </div>
                                    </Link>
                                </div>
                                <div className="background-gradient-circle-3" />
                                <div className="testimonials-item-grid" />
                            </div>
                            <div className="testimonials-item-padding" />
                        </div>
                    </div>
                </div>

                <div className="testimonials-content-bottom">
                    <div className="testimonials-content-bottom-buttons">
                        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
                        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
                    </div>
                    <div className="embla__progress">
                        <div
                            className="embla__progress__bar"
                            style={{ transform: `translate3d(${scrollProgress}%,0px,0px)` }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};